import { FC } from 'react';
import { cn } from '@shadcn/ui/lib/utils';
import { Card } from '@shadcn/ui/components/ui/card';
import { ContactAvatar } from '../../../../../components/contact-avatar';
import { Building2, EllipsisVertical, Sparkles } from 'lucide-react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { GetContactsWeightedScoresQuery } from '@zaplify/graphql';
import { formatDistanceToNow, subDays } from 'date-fns';
import { ActionType } from '@andsend/intelligence/shared';
import { htmlToText } from 'html-to-text';
import { Badge } from '@shadcn/ui/components/ui/badge';
import { ActionCardOptionsButton } from './action-card-options-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shadcn/ui/components/ui/tooltip';
import { ActionBadge } from '../../../../../components/action-badges/action-badge';

type ContactScoreData = GetContactsWeightedScoresQuery['contacts_weighted_scores'][number];

type ActionCardType =
    | 'message' // SEND_EMAIL, SEND_LINKEDIN_MESSAGE, SEND_FOLLOW_UP
    | 'phone' // PHONE_CALL, SCHEDULE_CALL
    | 'meeting' // MEETING, SCHEDULE_MEETING
    | 'other'; // Any other action type

type ActionToCardTypeMap = {
    SEND_EMAIL: 'message';
    SEND_LINKEDIN_MESSAGE: 'message';
    SEND_FOLLOW_UP: 'message';
    PHONE_CALL: 'phone';
    SCHEDULE_CALL: 'phone';
    MEETING: 'meeting';
    SCHEDULE_MEETING: 'meeting';
};

// Helper function to actually perform the mapping
export const mapActionToCardType = (actionType: ActionType): ActionCardType => {
    return (actionType as keyof ActionToCardTypeMap) in actionToCardTypeMap
        ? actionToCardTypeMap[actionType as keyof ActionToCardTypeMap]
        : 'other';
};

const actionToCardTypeMap: ActionToCardTypeMap = {
    SEND_EMAIL: 'message',
    SEND_LINKEDIN_MESSAGE: 'message',
    SEND_FOLLOW_UP: 'message',
    PHONE_CALL: 'phone',
    SCHEDULE_CALL: 'phone',
    MEETING: 'meeting',
    SCHEDULE_MEETING: 'meeting',
};

export const ActionCard: FC<{
    contact: ContactScoreData;
    score: number;
    openSnoozeDialogForContact: (contactFirstName: string, contactId: string) => void;
    className?: string;
}> = ({ contact, score, openSnoozeDialogForContact, className }) => {
    const pageContactId = useParams().contactId;
    const [searchParams] = useSearchParams();
    const threadId = searchParams.get('threadId');
    return (
        <Link
            to={{
                pathname: `/new/actions-v2/${mapActionToCardType(contact.topActionType as ActionType)}/${contact.id}`,
                search: threadId ? `?threadId=${threadId}` : '',
            }}
            className="w-full max-w-[min(70dvw,60rem)]"
        >
            <Card
                className={cn(
                    'group/action-card flex flex-col gap-4 w-full p-4 transition-all duration-100 cursor-pointer',
                    {
                        'bg-background-brand-primary-subtle/40': pageContactId === contact.id,
                        'hover:scale-[1.01] hover:-translate-y-1': pageContactId !== contact.id,
                    },
                    className
                )}
            >
                <div className="relative flex gap-2 items-center">
                    <ActionCardOptionsButton
                        contactId={contact.id}
                        openSnoozeDialogForContact={openSnoozeDialogForContact}
                        className="absolute -top-2 -right-2 opacity-100 duration-100 aria-expanded:opacity-100"
                    />
                    <div className="absolute -top-1.5 right-8">
                        {contact.urgent && (
                            <Badge
                                variant="warning"
                                title={`AI identified this as urgent on ${new Date(
                                    contact.metadataLastUpdatedAt
                                ).toLocaleDateString()}`}
                            >
                                !!
                            </Badge>
                        )}
                        {/* {contact.compositeScore && (
                                    <Tooltip delayDuration={500}>
                                        <TooltipTrigger asChild>
                                            <Badge variant="outline" className="text-xs">
                                                {contact.compositeScore.toFixed()}
                                            </Badge>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Contact priority score</p>
                                        </TooltipContent>
                                    </Tooltip>
                                )} */}
                        {/* {contact.priorityScore && (
                                            <Badge variant="outline" className="text-xs">
                                                {contact.priorityScore.toFixed()}
                                            </Badge>
                                        )} */}
                        {contact.contactType && (
                            <Tooltip delayDuration={500}>
                                <TooltipTrigger asChild>
                                    <Badge variant="outline" className="text-xs">
                                        {contact.contactType?.charAt(0).toUpperCase() +
                                            contact.contactType?.toLowerCase().replace('_', ' ').slice(1)}
                                    </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Contact type - {contact.contactType}</p>
                                    <p>Priority score - {contact.compositeScore}</p>
                                    <p>Relationship type - {contact.relationshipType}</p>
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                    <ContactAvatar fullName={contact.fullName} avatarUrl={contact.linkedinProfileImgUrl} size="lg" />
                    <div className="flex flex-col gap-1 w-full">
                        <div className="flex gap-1 items-center">
                            <p className="text-md font-medium capitalize">{contact.firstName}</p>
                            <p className="text-sm text-gray-500">•</p>
                            <p className="text-sm text-gray-500 capitalize line-clamp-1">{contact.organizationName}</p>
                        </div>
                        <div className="flex gap-1 justify-between w-full">
                            <span className="text-sm line-clamp-1 max-w-[70%]">
                                {contact.Messages?.[0]?.direction === 'in'
                                    ? contact.firstName.charAt(0).toUpperCase() + contact.firstName.slice(1)
                                    : 'You'}
                                : {htmlToText(contact.Messages?.[0]?.content)}
                            </span>
                            <span className="flex text-sm text-gray-500 flex-shrink-0">
                                {formatDistanceToNow(new Date(contact.Messages?.[0]?.sentOn || subDays(new Date(), 1)))}{' '}
                                ago
                            </span>
                        </div>
                    </div>
                </div>
                {/* <div className="flex gap-2 rounded p-1 px-2 bg-background-brand-secondary-section items-center">
                    <Sparkles className="w-3 h-3 text-text-brand-secondary flex-shrink-0" />
                    <p className="text-sm text-text-brand-secondary">{contact.recommendedActions?.[0]?.reason}</p>
                </div> */}
                <div className="flex gap-2 rounded p-2 border-t text-sm w-full">
                    <div className="flex gap-2 items-start mt-1 ">
                        <Sparkles className="w-4 h-4 text-text-brand-secondary flex-shrink-0" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <div className="flex gap-2 w-full justify-between">
                            <span className="font-medium text-text-brand-secondary">Agent analysis</span>
                            <div className="flex gap-2">
                                <ActionBadge
                                    prospectId={contact.prospectId}
                                    isCompleted={contact.lastCompletedActionAt !== null}
                                    recommendedAction={contact.recommendedActions?.[0]}
                                    lastMessageInput={contact.Messages?.[0]}
                                />
                            </div>
                        </div>
                        <span className="text-text-tertiary">{contact.recommendedActions?.[0]?.reason}</span>
                    </div>
                </div>
            </Card>
        </Link>
    );
};
